import { createSelector } from "reselect";
import { AppState } from "..";
import { InventoryState } from "src/interfaces/inventoryState.interface";
import { getVendors } from "src/store/vendor/selector";
import { filter } from "src/utils/filters";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import type { MappedTransaction } from "src/interfaces/transaction.interface";
import type { Inventory } from "src/interfaces/inventory.interface";

const getInventoryState = (state: AppState) => state.inventory;

export const inventoriesSelector = createSelector(
  [getInventoryState, getVendors],
  (inventory: InventoryState, vendors) => {
    return {
      ...inventory,
      items: inventory.items.map((item) => {
        if (item.vendor)
          return {
            ...item,
            vendorName: vendors.find((v) => v.id === item.vendor)?.name || "",
          };

        return item;
      }),
    };
  }
);

export const getInventory = createSelector(
  getInventoryState,
  (state) => state.items
);

export const getEarliestInventoryYear = createSelector(
  getInventory,
  (inventory) => {
    const minDate = inventory.reduce((result, item) => {
      if (!item.purchase_date) {
        return result;
      }
      const purchaseDate = new Date(item.purchase_date);
      if (isNaN(purchaseDate.valueOf())) {
        return result;
      }
      if (purchaseDate < result) {
        return purchaseDate;
      }
      return result;
    }, new Date());
    return +minDate.getFullYear();
  }
);

const InventoryFilterDefinition = {
  purchase_date: "date" as const,
};

export const filteredInventorySelector = createSelector(
  [
    getInventory,
    (_, models) => (models?.length > 0 ? models : undefined),
    effectiveUserTimeZoneSelector,
  ],
  (items, models, timeZone) =>
    models
      ? models.reduce(
          (items, model) =>
            model
              ? filter(InventoryFilterDefinition, items, model, {
                  dateComparisonTimeZone: timeZone,
                })
              : items,
          items
        )
      : items
);

export const inventoryByIdSelector = createSelector(
  [getInventory, (_, id) => id],
  (inventory, id) => {
    for (const item of inventory) {
      if (item.id === id) return item;
    }
  }
);

export function mapInventoryToTransaction(
  item: Inventory
): MappedTransaction | undefined {
  if (item.transaction_inventory_id) return;
  if (!item.purchase_date) return;

  return {
    id: `inventory-${item.id}`,
    collection: "inventory",
    collection_id: item.id,
    date: item.purchase_date,
    general_ledger_account: "Inventory",
    bank_account: item.bank_account || "Unassigned",
    description: item.item_title,
    amount: item.purchase_price,
    type: "inventory",
    charge_description: "",
  };
}

export const inventoryAsTransactionsSelector = createSelector(
  [getInventory],
  (inventory): MappedTransaction[] => {
    const transactions: MappedTransaction[] = [];
    for (const item of inventory) {
      const transaction = mapInventoryToTransaction(item);
      if (transaction) transactions.push(transaction);
    }
    return transactions;
  }
);
